import DateFnsUtils from '@date-io/date-fns'
import { yupResolver } from '@hookform/resolvers'
import CardContents from '@ifca-root/react-component/src/components/CardList/CardContents'
import MainHeader from '@ifca-root/react-component/src/components/Header/MainHeader'
import { VoiceTextField } from '@ifca-root/react-component/src/components/Input/CustomTextField'
import { ContentWrapper } from '@ifca-root/react-component/src/components/Layout/ContentWrapper'
import Loading from '@ifca-root/react-component/src/components/Loading/Loading'
import { TextField } from '@material-ui/core'
import { Autocomplete } from '@material-ui/lab'
import {
  KeyboardDatePicker,
  MuiPickersUtilsProvider,
} from '@material-ui/pickers'
import { ErrorDialog } from 'components/Dialog/ErrorDialog'
import { ExitConfirmationDialog } from 'components/Dialog/ExitConfirmationDialog'
import { AccCodeDropdownFullWidth } from 'components/Dropdown/AccCodeDropdown'
import { AccountFooter } from 'components/Footer/AccountFooter'
import SnackBarContext from 'containers/App/Store/SnackBarContext'
import {
  ApprovalStatus,
  useCreateFaAssetTransferByDepartmentMutation,
  useGetAssetRegistrationQuery,
  useGetCostCentreLazyQuery,
  useGetCurrentAssetTransferByDeptInfoLazyQuery,
  useGetDocumentDateValidationQuery,
  useGetFaLocationQuery,
  useLatestOpenPeriodCheckingDateQuery,
  useUpdateFaAssetTransferByDepartmentMutation,
} from 'generated/graphql'
import { SystemMsgs } from 'helpers/Messages/SystemMsg'
import React, { useContext, useEffect, useMemo, useState } from 'react'
import { Controller, useForm } from 'react-hook-form'
import NumberFormat from 'react-number-format'
import { useHistory, useLocation, useParams } from 'react-router-dom'
import * as yup from 'yup'

interface DepartmentQuantityInfo {
  cost_centre_id: string
  cost_centre_name?: string
  fa_location_id: string
  location_name?: string
  currentQty: number
  pendingTransferOutQty: number
  availableQty: number
}

interface CurrentAssetTransferInfo {
  registrationQuantity: number
  currentTotalQuantity: number
  totalDisposedQty: number
  departmentBreakdown: DepartmentQuantityInfo[]
  currentOriginalValue: number
  currentNetBookValue: number
}

export const AssetTransferByDepartmentForm = (props: any) => {
  const history = useHistory()
  const location = useLocation()
  const editData: any = location?.state
  const { formMode } = props
  const { CompanyID }: any = useParams()
  const user = JSON.parse(localStorage.getItem('loggedInUser'))

  /* -------------------------------------------- */
  /*                     STATE                    */
  /* -------------------------------------------- */
  const [errorDia, setErrorDia] = useState<boolean>(false)
  const [errMsg, setErrMsg] = useState<string>('')
  const [openExitConf, setOpenExitConf] = useState(false)
  const [record, setRecord] = useState(false)
  const [selectedAsset, setSelectedAsset] = useState<any>(
    editData?.Asset || null
  )
  const [selectedFromDepartment, setSelectedFromDepartment] = useState<any>(
    null
  )
  const [selectedToDepartment, setSelectedToDepartment] = useState<any>(null)
  const [
    currentInfo,
    setCurrentInfo,
  ] = useState<CurrentAssetTransferInfo | null>(null)
  const { setOpenSnackBar, setSnackBarMsg }: any = useContext(SnackBarContext)

  let mode = formMode
  if (formMode === 'resubmit') {
    mode = 'edit'
  }

  /* -------------------------------------------- */
  /*                   USE FORM                   */
  /* -------------------------------------------- */
  const assetTransferByDeptSchema = yup.object().shape({
    asset_no: yup.mixed().required('Asset is required'),
    from_department_id: yup
      .string()
      .required('Transfer From Department is required'),
    to_department_id: yup
      .string()
      .required('Transfer To Department is required'),
    from_location_id: yup
      .string()
      .required('Transfer From Location is required'),
    to_location_id: yup.string().required('Transfer To Location is required'),
    transfer_date: yup.date().required('Transfer Date is required'),
    transfer_qty: yup
      .number()
      .typeError('Transfer Quantity is required')
      .required('Transfer Quantity is required')
      .moreThan(0, 'Transfer Quantity must be greater than 0'),
    description: yup.string().required('Description is required'),
    reason: yup.string().required('Reason is required'),
  })

  const { register, setValue, control, handleSubmit, watch, errors } = useForm<
    any
  >({
    defaultValues: {
      transfer_date: editData?.transfer_date || new Date(),
      transfer_qty: editData?.transfer_qty || null,
      description: editData?.description || '',
      reason: editData?.reason || '',
    },
    mode: 'onSubmit',
    resolver: yupResolver(assetTransferByDeptSchema),
  })

  const watchFromLocationId = watch('from_location_id')
  const watchFromDepartmentId = watch('from_department_id')

  const sourceDepartmentQty = useMemo(() => {
    const deptId = watchFromDepartmentId || selectedFromDepartment?.CostCentreID
    if (!deptId) {
      return null
    }

    if (currentInfo?.departmentBreakdown) {
      const sourceDept = currentInfo.departmentBreakdown.find(
        dept => dept.cost_centre_id === deptId
      )
      return sourceDept?.availableQty ?? 0
    }

    return null
  }, [
    currentInfo?.departmentBreakdown,
    watchFromDepartmentId,
    selectedFromDepartment,
  ])

  /* -------------------------------------------- */
  /*                    QUERY                     */
  /* -------------------------------------------- */
  const {
    loading: assetsLoading,
    data: { getAssetRegistration } = { getAssetRegistration: [] },
  } = useGetAssetRegistrationQuery({
    fetchPolicy: 'network-only',
    variables: {
      CompanyID: CompanyID,
    },
    onError: ({ message }) => {
      const error = message?.substring(15)
      setErrMsg(error)
      setErrorDia(true)
    },
  })

  const [
    loadDepartments,
    { loading: departmentsLoading, data: departmentsData },
  ] = useGetCostCentreLazyQuery({
    fetchPolicy: 'network-only',
    variables: {
      CompanyID: CompanyID,
      IsLastNode: true,
    },
    onError: ({ message }) => {
      const error = message?.substring(15)
      setErrMsg(error)
      setErrorDia(true)
    },
  })

  const {
    loading: getFALocationLoading,
    data: { getFALocation } = { getFALocation: [] },
  } = useGetFaLocationQuery({
    fetchPolicy: 'network-only',
    variables: { CompanyID: CompanyID },
    onError: ({ message }) => {
      const error = message?.substring(15)
      setErrMsg(error)
      setErrorDia(true)
    },
  })

  const [
    getCurrentAssetTransferByDeptInfo,
    { loading: currentInfoLoading },
  ] = useGetCurrentAssetTransferByDeptInfoLazyQuery({
    fetchPolicy: 'network-only',
    onError: ({ message }) => {
      const error = message?.substring(15)
      setErrMsg(error)
      setErrorDia(true)
    },
    onCompleted: data => {
      if (data?.getCurrentAssetTransferByDeptInfo) {
        const info = data.getCurrentAssetTransferByDeptInfo as CurrentAssetTransferInfo
        setCurrentInfo(info)
        setValue('quantity', info.currentTotalQuantity)
      }
    },
  })

  const {
    loading: openPeriodCheckDateLoading,
    data: { latestOpenPeriodCheckingDate } = {
      latestOpenPeriodCheckingDate: {} as any,
    },
  } = useLatestOpenPeriodCheckingDateQuery({
    fetchPolicy: 'network-only',
    variables: { CompanyID },
    onError: ({ message }) => {
      const error = message?.substring(15)
      setErrMsg(error)
      setErrorDia(true)
    },
  })

  const {
    loading: docDateValidationLoading,
    data: { getDocumentDateValidation } = { getDocumentDateValidation: [] },
  } = useGetDocumentDateValidationQuery({
    fetchPolicy: 'network-only',
    onError: ({ message }) => {
      const error = message?.substring(15)
      setErrMsg(error)
      setErrorDia(true)
    },
  })

  const assetsList = useMemo(() => {
    return getAssetRegistration || []
  }, [getAssetRegistration])

  const departmentsList = useMemo(() => {
    if (!departmentsData?.getCostCentre) {
      loadDepartments()
      return []
    }
    return departmentsData.getCostCentre
  }, [departmentsData])

  useEffect(() => {
    if (
      editData?.from_department_id &&
      departmentsList.length > 0 &&
      !selectedFromDepartment
    ) {
      const dept = departmentsList.find(
        d => d.CostCentreID === editData.from_department_id
      )
      if (dept) {
        setSelectedFromDepartment(dept)
        setValue('from_department_id', dept.CostCentreID)
      }
    }
  }, [departmentsList, editData?.from_department_id])

  useEffect(() => {
    if (
      editData?.to_department_id &&
      departmentsList.length > 0 &&
      !selectedToDepartment
    ) {
      const dept = departmentsList.find(
        d => d.CostCentreID === editData.to_department_id
      )
      if (dept) {
        setSelectedToDepartment(dept)
        setValue('to_department_id', dept.CostCentreID)
      }
    }
  }, [departmentsList, editData?.to_department_id])

  useEffect(() => {
    if (
      (formMode === 'edit' || formMode === 'resubmit') &&
      editData?.fa_asset_id &&
      assetsList.length > 0
    ) {
      const asset = assetsList.find(a => a.fa_asset_id === editData.fa_asset_id)
      if (asset) {
        setSelectedAsset(asset)
        setValue('asset_no', asset)
        setValue('asset_name', asset.name || '')
        setValue('start_date', asset.start_use_date)
        setValue('model_no', asset.model_no || '')

        getCurrentAssetTransferByDeptInfo({
          variables: {
            fa_asset_id: asset.fa_asset_id,
            exclude_transfer_id: editData.transfer_department_id || null,
          },
        })
      }
    }
  }, [formMode, editData, assetsList])

  /* -------------------------------------------- */
  /*                   MUTATION                   */
  /* -------------------------------------------- */
  const [
    createTransfer,
    { loading: createLoading },
  ] = useCreateFaAssetTransferByDepartmentMutation({
    onError: ({ message }) => {
      const error = message?.substring(15)
      setErrMsg(error)
      setErrorDia(true)
    },
    onCompleted: () => {
      setOpenSnackBar(true)
      setSnackBarMsg(SystemMsgs.createNewRecord())
      history.push(
        `/fixed-assets/${CompanyID}/assets-change/asset-transfer-by-department`
      )
    },
  })

  const [
    updateTransfer,
    { loading: updateLoading },
  ] = useUpdateFaAssetTransferByDepartmentMutation({
    onError: ({ message }) => {
      const error = message?.substring(15)
      setErrMsg(error)
      setErrorDia(true)
    },
    onCompleted: () => {
      setOpenSnackBar(true)
      setSnackBarMsg(SystemMsgs.updateRecord())
      history.push(
        `/fixed-assets/${CompanyID}/assets-change/asset-transfer-by-department`
      )
    },
  })

  /* -------------------------------------------- */
  /*                SUBMIT HANDLERS               */
  /* -------------------------------------------- */
  const onSubmit = (data: any, approvalStatus: ApprovalStatus) => {
    if (approvalStatus === ApprovalStatus.Submit) {
      if (isPeriodClosed) {
        setErrMsg(
          'Cannot submit. The accounting period is closed or does not exist. ' +
            'Please select a date within the open financial period ' +
            (latestOpenPeriodCheckingDate?.StartDate &&
            latestOpenPeriodCheckingDate?.EndDate
              ? `(${latestOpenPeriodCheckingDate.StartDate} to ${latestOpenPeriodCheckingDate.EndDate}).`
              : '.')
        )
        setErrorDia(true)
        return
      }

      if (isDocDateInvalid) {
        setErrMsg(
          'Cannot submit. The transfer date is outside the allowed validation range. ' +
            'Please select a date within the valid period.'
        )
        setErrorDia(true)
        return
      }
    }

    const currentAsset = selectedAsset || editData?.Asset
    const currentFromDepartment =
      selectedFromDepartment ||
      (editData?.from_department_id
        ? { CostCentreID: editData.from_department_id }
        : null)
    const currentToDepartment =
      selectedToDepartment ||
      (editData?.to_department_id
        ? { CostCentreID: editData.to_department_id }
        : null)

    if (!currentAsset) {
      setErrMsg('Please select an asset')
      setErrorDia(true)
      return
    }

    if (!currentFromDepartment) {
      setErrMsg('Please select transfer from department')
      setErrorDia(true)
      return
    }

    if (!currentToDepartment) {
      setErrMsg('Please select transfer to department')
      setErrorDia(true)
      return
    }

    if (
      currentFromDepartment.CostCentreID === currentToDepartment.CostCentreID
    ) {
      setErrMsg('Transfer from and to departments must be different')
      setErrorDia(true)
      return
    }

    if (!data.from_location_id) {
      setErrMsg('Please select transfer from location')
      setErrorDia(true)
      return
    }

    if (!data.to_location_id) {
      setErrMsg('Please select transfer to location')
      setErrorDia(true)
      return
    }

    const transferQty = parseFloat(data.transfer_qty)

    if (!transferQty || transferQty <= 0) {
      setErrMsg('Please enter a valid transfer quantity')
      setErrorDia(true)
      return
    }

    const selectedSourceDept = currentInfo?.departmentBreakdown?.find(
      dept => dept.cost_centre_id === currentFromDepartment?.CostCentreID
    )
    const availableQty =
      selectedSourceDept?.availableQty ??
      currentInfo?.currentTotalQuantity ??
      currentAsset?.quantity ??
      0
    if (transferQty > availableQty) {
      setErrMsg(
        `Transfer quantity (${transferQty}) cannot exceed available quantity at source department (${availableQty})`
      )
      setErrorDia(true)
      return
    }

    const input = {
      transfer_department_id: editData?.transfer_department_id || undefined,
      fa_asset_id: selectedAsset?.fa_asset_id || editData?.fa_asset_id,
      company_id: CompanyID,
      from_department_id:
        selectedFromDepartment?.CostCentreID || editData?.from_department_id,
      to_department_id:
        selectedToDepartment?.CostCentreID || editData?.to_department_id,
      from_location_id: data.from_location_id,
      to_location_id: data.to_location_id,
      transfer_qty: transferQty,
      source_qty_before: availableQty,
      source_qty_after: availableQty - transferQty,
      destination_qty_before: 0,
      destination_qty_after: transferQty,
      transfer_date: data.transfer_date,
      description: data.description,
      reason: data.reason,
      ApprovalStatus: approvalStatus,
    }

    if (formMode === 'add') {
      createTransfer({ variables: { input } })
    } else {
      updateTransfer({ variables: { input } })
    }
  }

  const handleAssetChange = (value: any) => {
    if (value) {
      setSelectedAsset(value)
      setValue('asset_no', value)
      setValue('asset_name', value?.name)
      setValue('start_date', value?.start_use_date)
      setValue('model_no', value?.model_no)

      getCurrentAssetTransferByDeptInfo({
        variables: {
          fa_asset_id: value.fa_asset_id,
          exclude_transfer_id: editData?.transfer_department_id || null,
        },
      })
    } else {
      setSelectedAsset(null)
      setCurrentInfo(null)
      setValue('asset_no', null)
      setValue('asset_name', '')
      setValue('start_date', null)
      setValue('model_no', '')
      setValue('quantity', '')
    }
  }

  const handleFromDepartmentChange = (value: any) => {
    if (value) {
      setSelectedFromDepartment(value)
      setValue('from_department_id', value?.CostCentreID || '')
      setValue('from_department_name', value?.Name)
      setValue('from_department_code', value?.Code)

      if (selectedAsset) {
        getCurrentAssetTransferByDeptInfo({
          variables: {
            fa_asset_id: selectedAsset.fa_asset_id,
            exclude_transfer_id: editData?.transfer_department_id || null,
          },
        })
      }
    } else {
      setSelectedFromDepartment(null)
      setValue('from_department_id', '')
      setValue('from_department_name', '')
      setValue('from_department_code', '')
    }
  }

  const handleToDepartmentChange = (value: any) => {
    if (value) {
      setSelectedToDepartment(value)
      setValue('to_department_id', value?.CostCentreID || '')
      setValue('to_department_name', value?.Name)
      setValue('to_department_code', value?.Code)
    } else {
      setSelectedToDepartment(null)
      setValue('to_department_id', '')
      setValue('to_department_name', '')
      setValue('to_department_code', '')
    }
  }

  /* -------------------------------------------- */
  /*               PERIOD VALIDATION              */
  /* -------------------------------------------- */
  const watchTransferDate =
    watch('transfer_date') || editData?.transfer_date || new Date()
  const transferDateStr = (() => {
    const d = new Date(watchTransferDate)
    const year = d.getFullYear()
    const month = (d.getMonth() + 1).toString().padStart(2, '0')
    const day = d
      .getDate()
      .toString()
      .padStart(2, '0')
    return `${year}-${month}-${day}`
  })()

  const isPeriodClosed =
    latestOpenPeriodCheckingDate?.StartDate &&
    latestOpenPeriodCheckingDate?.EndDate
      ? transferDateStr < latestOpenPeriodCheckingDate?.StartDate ||
        transferDateStr > latestOpenPeriodCheckingDate?.EndDate
      : false

  const today = new Date()
  const monthsBefore = getDocumentDateValidation?.[0]?.MonthsBefore ?? 12
  const monthsAfter = getDocumentDateValidation?.[0]?.MonthsAfter ?? 1

  const minValidDate = new Date(today)
  minValidDate.setMonth(minValidDate.getMonth() - monthsBefore)
  minValidDate.setHours(0, 0, 0, 0)

  const maxValidDate = new Date(today)
  maxValidDate.setMonth(maxValidDate.getMonth() + monthsAfter)
  maxValidDate.setHours(23, 59, 59, 999)

  const transferDate = new Date(watchTransferDate)
  const isDocDateInvalid =
    transferDate.getTime() < minValidDate.getTime() ||
    transferDate.getTime() > maxValidDate.getTime()

  /* -------------------------------------------- */
  /*                    FOOTER                    */
  /* -------------------------------------------- */
  const draftFooterOption = {
    name: 'Save as Draft',
    onClick: handleSubmit(data => onSubmit(data, ApprovalStatus.Active)),
    color: 'primary',
    props: { type: 'button' },
  }

  const submitFooterOption = {
    name: 'Submit',
    onClick: handleSubmit(data => onSubmit(data, ApprovalStatus.Submit)),
    color: 'primary',
    props: { type: 'button' },
  }

  let footerOptions: any[]
  if (formMode === 'add' || formMode === 'edit' || formMode === 'resubmit') {
    footerOptions = [draftFooterOption, submitFooterOption]
  } else {
    footerOptions = []
  }

  return (
    <>
      {(assetsLoading ||
        departmentsLoading ||
        getFALocationLoading ||
        createLoading ||
        updateLoading ||
        currentInfoLoading ||
        openPeriodCheckDateLoading ||
        docDateValidationLoading) && <Loading />}
      <MainHeader
        mainBtn="close"
        onClick={() => {
          history.push(
            `/fixed-assets/${CompanyID}/assets-change/asset-transfer-by-department`
          )
        }}
        smTitle={'Fixed Assets'}
        title={user?.companyName}
        routeSegments={[
          { name: 'Fixed Assets' },
          { name: 'Assets Change' },
          { name: 'Asset Transfer By Department', current: true },
        ]}
        rightRouteSegments={[
          {
            name:
              formMode === 'add'
                ? 'New'
                : formMode === 'resubmit'
                ? 'Resubmit'
                : 'Edit',
            current: true,
          },
        ]}
      />

      <ContentWrapper float footer>
        <CardContents
          section={{
            header: {
              title: 'Asset Information',
            },
          }}
        >
          {!assetsLoading && (
            <Controller
              render={({ value, onChange }) => {
                const currentAsset = assetsList
                  ?.sort((a, b) => a?.DocNo?.localeCompare(b?.DocNo))
                  ?.find(
                    asset =>
                      asset?.fa_asset_id ===
                      (selectedAsset?.fa_asset_id || editData?.fa_asset_id)
                  )

                return (
                  <Autocomplete
                    options={
                      assetsList?.sort((a, b) => {
                        return a?.DocNo?.localeCompare(b?.DocNo)
                      }) || []
                    }
                    value={currentAsset || null}
                    getOptionLabel={option =>
                      option ? `${option?.DocNo} - ${option?.name}` : ''
                    }
                    fullWidth
                    onChange={(event, value: any) => handleAssetChange(value)}
                    disabled={mode === 'approve-reject'}
                    PopperComponent={AccCodeDropdownFullWidth}
                    renderOption={props => {
                      return (
                        <div>
                          <div>
                            <span className="xsTitle">{props?.DocNo}</span>
                            <span
                              className="desc"
                              style={{ marginLeft: '8px' }}
                            >
                              {props?.name}
                            </span>
                          </div>
                        </div>
                      )
                    }}
                    renderInput={(params: any) => {
                      return (
                        <TextField
                          {...params}
                          label="Asset"
                          InputLabelProps={
                            currentAsset ? { shrink: true } : undefined
                          }
                          style={{ width: '100%' }}
                          required
                          margin="normal"
                          disabled={mode === 'approve-reject'}
                          helperText={errors?.asset_no?.message}
                          error={errors?.asset_no ? true : false}
                        />
                      )
                    }}
                  />
                )
              }}
              name="asset_no"
              control={control}
              margin="normal"
            />
          )}

          <Controller
            as={TextField}
            name="asset_name"
            label="Asset Name"
            autoComplete="off"
            control={control}
            fullWidth
            margin="dense"
            defaultValue={editData?.Asset?.name || 'N/A'}
            InputProps={{
              disableUnderline: true,
              readOnly: true,
            }}
          />

          <MuiPickersUtilsProvider utils={DateFnsUtils}>
            <Controller
              render={({ value, onChange }) => (
                <KeyboardDatePicker
                  value={value}
                  onChange={onChange}
                  label="Asset Start Date"
                  format="dd/MM/yyyy"
                  readOnly
                  margin="dense"
                  className="left"
                  allowKeyboardControl
                  KeyboardButtonProps={{
                    style: { display: 'none' },
                  }}
                  InputProps={{
                    disableUnderline: true,
                    readOnly: true,
                  }}
                />
              )}
              name="start_date"
              control={control}
              defaultValue={editData?.Asset?.start_use_date || new Date()}
            />
          </MuiPickersUtilsProvider>

          <Controller
            as={TextField}
            name="model_no"
            label="Model No"
            autoComplete="off"
            control={control}
            fullWidth
            margin="dense"
            className="right"
            defaultValue={editData?.Asset?.model_no || 'N/A'}
            InputProps={{
              disableUnderline: true,
              readOnly: true,
            }}
          />

          <Controller
            render={({ value }) => (
              <NumberFormat
                customInput={TextField as any}
                value={
                  sourceDepartmentQty !== null
                    ? sourceDepartmentQty
                    : currentInfo?.currentTotalQuantity ?? value ?? 0
                }
                thousandSeparator
                decimalScale={2}
                fixedDecimalScale
                label="Qty (Transfer from)"
                fullWidth
                margin="dense"
                InputProps={{
                  disableUnderline: true,
                  readOnly: true,
                }}
                // helperText={
                //   sourceDepartmentQty !== null
                //     ? sourceDepartmentQty === 0
                //       ? 'This department does not have any quantity for this asset'
                //       : 'Available quantity at selected source department'
                //     : 'Select source department to see available quantity'
                // }
              />
            )}
            name="quantity"
            control={control}
            defaultValue={editData?.Asset?.quantity || '0'}
          />
        </CardContents>

        <CardContents
          section={{
            header: {
              title: 'Department Transfer',
            },
          }}
        >
          {!departmentsLoading && (
            <Controller
              render={({ value, onChange }) => {
                const currentFromDepartment = departmentsList
                  ?.sort((a, b) => a?.Code?.localeCompare(b?.Code))
                  ?.find(
                    dept =>
                      dept?.CostCentreID ===
                      (selectedFromDepartment?.CostCentreID ||
                        editData?.from_department_id)
                  )

                return (
                  <Autocomplete
                    options={
                      departmentsList?.sort((a, b) =>
                        a?.Code?.localeCompare(b?.Code)
                      ) || []
                    }
                    value={currentFromDepartment || null}
                    getOptionLabel={option =>
                      option ? `${option?.Code} - ${option?.Name}` : ''
                    }
                    fullWidth
                    onChange={(event, value: any) =>
                      handleFromDepartmentChange(value)
                    }
                    disabled={mode === 'approve-reject'}
                    PopperComponent={AccCodeDropdownFullWidth}
                    renderOption={props => {
                      return (
                        <div>
                          <div>
                            <span className="xsTitle">{props?.Code}</span>
                          </div>
                          <div className="desc">{props?.Name}</div>
                        </div>
                      )
                    }}
                    renderInput={(params: any) => {
                      return (
                        <TextField
                          {...params}
                          label="Transfer From Department"
                          InputLabelProps={
                            currentFromDepartment ? { shrink: true } : undefined
                          }
                          style={{ width: '100%' }}
                          required
                          margin="normal"
                          disabled={mode === 'approve-reject'}
                          helperText={errors?.from_department_id?.message}
                          error={errors?.from_department_id ? true : false}
                        />
                      )
                    }}
                  />
                )
              }}
              name="from_department_id"
              control={control}
              margin="normal"
            />
          )}

          {!departmentsLoading && (
            <Controller
              render={({ value, onChange }) => {
                const currentToDepartment = departmentsList
                  ?.sort((a, b) => a?.Code?.localeCompare(b?.Code))
                  ?.find(
                    dept =>
                      dept?.CostCentreID ===
                      (selectedToDepartment?.CostCentreID ||
                        editData?.to_department_id)
                  )

                return (
                  <Autocomplete
                    options={
                      departmentsList?.sort((a, b) =>
                        a?.Code?.localeCompare(b?.Code)
                      ) || []
                    }
                    value={currentToDepartment || null}
                    getOptionLabel={option =>
                      option ? `${option?.Code} - ${option?.Name}` : ''
                    }
                    fullWidth
                    onChange={(event, value: any) =>
                      handleToDepartmentChange(value)
                    }
                    disabled={mode === 'approve-reject'}
                    PopperComponent={AccCodeDropdownFullWidth}
                    renderOption={props => {
                      return (
                        <div>
                          <div>
                            <span className="xsTitle">{props?.Code}</span>
                          </div>
                          <div className="desc">{props?.Name}</div>
                        </div>
                      )
                    }}
                    renderInput={(params: any) => {
                      return (
                        <TextField
                          {...params}
                          label="Transfer To Department"
                          InputLabelProps={
                            currentToDepartment ? { shrink: true } : undefined
                          }
                          style={{ width: '100%' }}
                          required
                          margin="normal"
                          disabled={mode === 'approve-reject'}
                          helperText={errors?.to_department_id?.message}
                          error={errors?.to_department_id ? true : false}
                        />
                      )
                    }}
                  />
                )
              }}
              name="to_department_id"
              control={control}
              margin="normal"
            />
          )}

          {!getFALocationLoading && (
            <Controller
              render={({ value, onChange }) => {
                const locationOptions =
                  (getFALocation?.filter(
                    loc =>
                      typeof loc === 'object' &&
                      loc !== null &&
                      'fa_location_id' in loc
                  ) as Array<{
                    __typename?: 'FALocationEntity'
                    name: string
                    description?: string
                    fa_location_id: string
                    RecordStatus: string
                  }>) || []
                const selectedFromLocation = locationOptions
                  .sort((a, b) => a.name.localeCompare(b.name))
                  .find(loc => loc.fa_location_id === value)

                return (
                  <Autocomplete
                    options={locationOptions.sort((a, b) =>
                      a.name.localeCompare(b.name)
                    )}
                    value={selectedFromLocation || null}
                    getOptionLabel={option =>
                      option
                        ? `${option.name}${
                            option.description ? ' | ' + option.description : ''
                          }`
                        : ''
                    }
                    fullWidth
                    onChange={(event, newValue: any) => {
                      onChange(newValue?.fa_location_id || '')
                    }}
                    disabled={mode === 'approve-reject'}
                    PopperComponent={AccCodeDropdownFullWidth}
                    renderOption={option => (
                      <div>
                        <div>
                          <span className="xsTitle">{option?.name}</span>
                          {option?.description && (
                            <span className="desc">
                              {' '}
                              - {option.description}
                            </span>
                          )}
                        </div>
                      </div>
                    )}
                    renderInput={params => (
                      <TextField
                        {...params}
                        label="Transfer From Location"
                        InputLabelProps={
                          selectedFromLocation ? { shrink: true } : undefined
                        }
                        margin="normal"
                        required
                        disabled={mode === 'approve-reject'}
                        helperText={errors?.from_location_id?.message}
                        error={errors?.from_location_id ? true : false}
                      />
                    )}
                  />
                )
              }}
              name="from_location_id"
              control={control}
              defaultValue={editData?.from_location_id || ''}
              required
            />
          )}

          {!getFALocationLoading && (
            <Controller
              render={({ value, onChange }) => {
                const locationOptions =
                  (getFALocation?.filter(
                    loc =>
                      typeof loc === 'object' &&
                      loc !== null &&
                      'fa_location_id' in loc
                  ) as Array<{
                    __typename?: 'FALocationEntity'
                    name: string
                    description?: string
                    fa_location_id: string
                    RecordStatus: string
                  }>) || []
                const selectedToLocation = locationOptions
                  .sort((a, b) => a.name.localeCompare(b.name))
                  .find(loc => loc.fa_location_id === value)

                return (
                  <Autocomplete
                    options={locationOptions.sort((a, b) =>
                      a.name.localeCompare(b.name)
                    )}
                    value={selectedToLocation || null}
                    getOptionLabel={option =>
                      option
                        ? `${option.name}${
                            option.description ? ' | ' + option.description : ''
                          }`
                        : ''
                    }
                    fullWidth
                    onChange={(event, newValue: any) => {
                      onChange(newValue?.fa_location_id || '')
                    }}
                    disabled={mode === 'approve-reject'}
                    PopperComponent={AccCodeDropdownFullWidth}
                    renderOption={option => (
                      <div>
                        <div>
                          <span className="xsTitle">{option?.name}</span>
                          {option?.description && (
                            <span className="desc">
                              {' '}
                              - {option.description}
                            </span>
                          )}
                        </div>
                      </div>
                    )}
                    renderInput={params => (
                      <TextField
                        {...params}
                        label="Transfer To Location"
                        InputLabelProps={
                          selectedToLocation ? { shrink: true } : undefined
                        }
                        margin="normal"
                        required
                        disabled={mode === 'approve-reject'}
                        helperText={errors?.to_location_id?.message}
                        error={errors?.to_location_id ? true : false}
                      />
                    )}
                  />
                )
              }}
              name="to_location_id"
              control={control}
              defaultValue={editData?.to_location_id || ''}
              required
            />
          )}

          <MuiPickersUtilsProvider utils={DateFnsUtils}>
            <Controller
              render={({ value, onChange }) => (
                <KeyboardDatePicker
                  value={value}
                  onChange={onChange}
                  label="Transfer Date"
                  format="dd/MM/yyyy"
                  margin="normal"
                  allowKeyboardControl
                  KeyboardButtonProps={{
                    'aria-label': 'change date',
                  }}
                  required
                  disabled={mode === 'approve-reject'}
                />
              )}
              name="transfer_date"
              control={control}
              defaultValue={editData?.transfer_date || new Date()}
            />
          </MuiPickersUtilsProvider>

          <Controller
            render={({ value, onChange }) => (
              <NumberFormat
                customInput={TextField as any}
                value={value}
                onValueChange={({ floatValue }) => onChange(floatValue)}
                thousandSeparator
                decimalScale={2}
                fixedDecimalScale
                label="Transfer Quantity"
                fullWidth
                margin="normal"
                required
                disabled={mode === 'approve-reject'}
                helperText={
                  errors?.transfer_qty?.message ||
                  (sourceDepartmentQty !== null
                    ? `Max: ${sourceDepartmentQty}`
                    : currentInfo
                    ? `Max: ${currentInfo.currentTotalQuantity}`
                    : '')
                }
                error={errors?.transfer_qty ? true : false}
              />
            )}
            name="transfer_qty"
            control={control}
            defaultValue={editData?.transfer_qty || ''}
          />

          <VoiceTextField
            mounted={true}
            label="Description"
            name="description"
            setValue={setValue}
            record={record}
            setRecord={setRecord}
            control={control}
            controllerProps={{
              autoComplete: 'off',
              defaultValue: editData?.description || '',
              helperText: errors?.description?.message,
              error: errors?.description ? true : false,
            }}
            disabled={mode === 'approve-reject'}
            required
          />

          <VoiceTextField
            mounted={true}
            label="Reason"
            name="reason"
            setValue={setValue}
            record={record}
            setRecord={setRecord}
            control={control}
            controllerProps={{
              autoComplete: 'off',
              defaultValue: editData?.reason || '',
              helperText: errors?.reason?.message,
              error: errors?.reason ? true : false,
            }}
            disabled={mode === 'approve-reject'}
            required
          />
        </CardContents>
      </ContentWrapper>

      {footerOptions?.length > 0 && (
        <AccountFooter options={[...footerOptions]} />
      )}

      <ErrorDialog
        errorDia={errorDia}
        setErrorDia={setErrorDia}
        errorMsg={errMsg}
        errorHeaderMsg={'Error'}
      />

      <ExitConfirmationDialog
        openExitConf={openExitConf}
        setOpenExitConf={setOpenExitConf}
        onConfirm={() => {
          history.push(
            `/fixed-assets/${CompanyID}/assets-change/asset-transfer-by-department`
          )
        }}
      />
    </>
  )
}
